import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wand2 } from 'lucide-react';
import { getSuggestions } from '@/services/geminiService';

interface InspirationHubProps {
  currentQuery: string;
  onSuggestionClick: (suggestion: string, mode: 'append' | 'replace') => void;
}

const InspirationHub: React.FC<InspirationHubProps> = ({ 
  currentQuery, 
  onSuggestionClick 
}) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (currentQuery.trim().length > 3) {
      // Race guard: only the latest debounce's response may commit state —
      // a slow earlier request must not overwrite fresher suggestions.
      let isCurrent = true;
      const debounce = setTimeout(async () => {
        setIsLoading(true);
        try {
          const aiSuggestions = await getSuggestions(currentQuery, 4);
          if (isCurrent && aiSuggestions && aiSuggestions.length > 0) {
            setSuggestions(aiSuggestions);
          }
        } catch {
        } finally {
          if (isCurrent) setIsLoading(false);
        }
      }, 500);
      return () => {
        isCurrent = false;
        clearTimeout(debounce);
      };
    } else {
      setSuggestions([]);
    }
  }, [currentQuery]);

  const handleClick = (e: React.MouseEvent, suggestion: string) => {
    e.preventDefault();
    e.stopPropagation();
    onSuggestionClick(suggestion, 'append');
  };

  if (!currentQuery.trim() || currentQuery.trim().length < 3) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="mt-4"
    >
      <div className="flex items-center gap-2 mb-2">
        <Wand2 className="w-3.5 h-3.5 text-gray-400" />
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Add to your idea
        </span>
        {isLoading && (
          <div className="w-3 h-3 border-2 border-gray-300 border-t-orange-500 rounded-full animate-spin" />
        )}
      </div>
      
      <AnimatePresence mode="wait">
        {suggestions.length > 0 && (
          <motion.div
            key={suggestions.join(',')}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-wrap gap-2"
          >
            {suggestions.map((suggestion, i) => (
              <motion.button
                key={i}
                type="button"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05 }}
                onClick={(e) => handleClick(e, suggestion)}
                className="px-3.5 py-2 min-h-[44px] inline-flex items-center text-sm bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-full border border-gray-200 dark:border-gray-700 hover:border-orange-300 dark:hover:border-orange-600 hover:text-orange-600 dark:hover:text-orange-400 transition-all shadow-sm hover:shadow"
              >
                + {suggestion}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default InspirationHub;
